/**
 * Application Service
 * Handles all member application related operations
 * Implements the complete MemberApplicationViewSet API
 */

import { apiRequest } from '../utils/apiResponseHandler';
import { getApiUrl } from '../config/api';

const applicationService = {
  // ==================== MEMBER APPLICATION MANAGEMENT ====================
  // Implements all endpoints from MemberApplicationViewSet

  /**
   * List Applications
   * GET /api/members/applications/
   * Retrieves all member applications with optional filtering and pagination
   */
  async getApplications(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    const url = queryParams.toString() 
      ? `${getApiUrl('APPLICATIONS')}?${queryParams.toString()}`
      : getApiUrl('APPLICATIONS');
    
    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  /**
   * Get Application Details
   * GET /api/members/applications/{id}/
   * Retrieve details of a specific application
   */
  async getApplicationById(applicationId) {
    return await apiRequest(`${getApiUrl('APPLICATIONS')}${applicationId}/`, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  /**
   * Check whether an email is already registered to a member
   * GET /api/members/check-email/?email=<addr>&exclude_id=<pk>
   * Used by the application form for real-time duplicate detection.
   * Returns the apiRequest result; the body is { exists: boolean } in response.data.
   */
  async checkEmailRegistered(email, excludeId = null) {
    const params = new URLSearchParams({ email });
    if (excludeId) {
      params.append('exclude_id', excludeId);
    }
    return await apiRequest(`${getApiUrl('CHECK_EMAIL')}?${params.toString()}`, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: false
    });
  },

  /**
   * Create Application
   * POST /api/members/applications/
   * Create a new member application manually
   */
  async createApplication(applicationData) {
    return await apiRequest(getApiUrl('APPLICATIONS'), {
      method: 'POST',
      body: applicationData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  /**
   * Update Application
   * PUT /api/members/applications/{id}/
   * Update all fields of an application
   */
  async updateApplication(applicationId, applicationData) {
    return await apiRequest(`${getApiUrl('APPLICATIONS')}${applicationId}/`, {
      method: 'PUT',
      body: applicationData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  /**
   * Partial Update Application
   * PATCH /api/members/applications/{id}/
   * Update specific fields of an application
   */
  async partialUpdateApplication(applicationId, applicationData) {
    return await apiRequest(`${getApiUrl('APPLICATIONS')}${applicationId}/`, {
      method: 'PATCH',
      body: applicationData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  /**
   * Delete Application
   * DELETE /api/members/applications/{id}/
   * Delete an application permanently
   */
  async deleteApplication(applicationId) {
    return await apiRequest(`${getApiUrl('APPLICATIONS')}${applicationId}/`, {
      method: 'DELETE',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  /**
   * Review Application (Custom Action)
   * POST /api/members/applications/{id}/review/
   * Approve or reject an application (admin action)
   */
  async reviewApplication(applicationId, reviewData) {
    return await apiRequest(`${getApiUrl('APPLICATIONS')}${applicationId}/review/`, {
      method: 'POST',
      body: reviewData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  /**
   * Send Edit Link (Custom Action)
   * POST /api/members/applications/{id}/send_edit_link/
   * Send a pre-filled form edit link to the customer
   */
  async sendEditLink(applicationId, recipientEmail = null) {
    const body = recipientEmail ? { recipient_email: recipientEmail } : {};
    return await apiRequest(`${getApiUrl('APPLICATIONS')}${applicationId}/send_edit_link/`, {
      method: 'POST',
      body: body,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },


  // ==================== HELPER METHODS ====================

  /**
   * Get Member Statistics
   * Helper method to get application-related statistics
   */
  async getMemberStatistics() {
    return await apiRequest(`${getApiUrl('MEMBER_STATS')}`, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  }
};

export default applicationService;